import type { DetailPageContext, ListingPageContext, PageRootContext } from '#layers/block-engine';
import type { CompositePrimaryKey, ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model';
import type { ReadManyQuery } from '~/composables/use-items';
import type { CollectionCodeDataItem } from '~/utils/code-snippet';
import { createBlockRootContextProperty } from '#layers/block-engine';

export interface ListingQueryBlockRootContext {
	/**
	 * Collection used by the published listing query.
	 */
	collection?: ResolvedMonospaceCollection;
	/**
	 * Read-many query produced by the active listing layout.
	 */
	query?: ReadManyQuery;
}

export interface FormBlockRootContext {
	/**
	 * Collection used by the published form.
	 */
	collection?: ResolvedMonospaceCollection;
	/**
	 * Fields currently rendered by the form.
	 */
	fields?: Array<ResolvedMonospacePrimitiveField | ResolvedMonospaceSingleRelationField>;
	/**
	 * Item id currently loaded by the form.
	 */
	currentItemId?: CompositePrimaryKey | null;
	/**
	 * Current form values without framework metadata fields.
	 */
	currentValues?: CollectionCodeDataItem;
}

const listingQueryBlockRootContext = createBlockRootContextProperty<ListingQueryBlockRootContext>('listingQuery');
const formBlockRootContext = createBlockRootContextProperty<FormBlockRootContext>('form');
const pageTypeBlockRootContext = createBlockRootContextProperty<PageRootContext['pageType']>('pageType');
const collectionIdBlockRootContext = createBlockRootContextProperty<DetailPageContext['collectionId'] | ListingPageContext['collectionId']>('collectionId');
const itemIdBlockRootContext = createBlockRootContextProperty<DetailPageContext['itemId']>('itemId');

/**
 * Publishes listing query details from a rendered listing layout.
 */
export const exposeListingQueryToBlockRootContext = listingQueryBlockRootContext.expose;

/**
 * Reads listing query details from registered root renders.
 */
export const consumeListingQueryBlockRootContext = listingQueryBlockRootContext.consume;

/**
 * Publishes form details from a rendered form block.
 */
export const exposeFormToBlockRootContext = formBlockRootContext.expose;

/**
 * Reads form details from registered root renders.
 */
export const consumeFormBlockRootContext = formBlockRootContext.consume;

/**
 * Reads page type values from registered root renders.
 */
export const consumePageTypeBlockRootContext = pageTypeBlockRootContext.consume;

/**
 * Reads collection id values from registered root renders.
 */
export const consumeCollectionIdBlockRootContext = collectionIdBlockRootContext.consume;

/**
 * Reads item id values from registered root renders.
 */
export const consumeItemIdBlockRootContext = itemIdBlockRootContext.consume;
